import React from 'react';
import { connect } from 'react-redux';
import classNames from 'classnames';

import { FormattedMessage } from '../../util/reactIntl';
import { selectCartItemCount, selectCartInitialized } from '../../ducks/cart.duck';
import NamedLink from '../NamedLink/NamedLink';

import css from './CartIcon.module.css';

const MAX_DISPLAYED = 99;

/**
 * CL-CART-01 — header cart icon and item count (requirement 3).
 *
 * The badge is deliberately absent until the cart has initialized on the client. The cart
 * lives in browser storage, so the server cannot know it during SSR; rendering "0" and
 * then flipping to "3" would flicker on every page load (brief L.7, AC-8).
 *
 * The link was deliberately absent until Task 7: `NamedLink name="CartPage"` throws
 * `Path "CartPage" was not found` (`src/util/routes.js:15`) until that route exists, which
 * would have crashed the header on every page. The route now exists, so it is a link.
 */
export const CartIconComponent = props => {
  const { className, rootClassName, itemCount, initialized } = props;
  const showBadge = initialized && itemCount > 0;
  const label = itemCount > MAX_DISPLAYED ? `${MAX_DISPLAYED}+` : `${itemCount}`;

  return (
    <NamedLink name="CartPage" className={classNames(rootClassName || css.root, className)}>
      <svg className={css.icon} width="24" height="24" viewBox="0 0 24 24" aria-hidden="true">
        <path
          d="M6 6h15l-1.5 9h-12L6 6Zm0 0L5 3H2m6.5 17a1 1 0 1 0 0 2 1 1 0 0 0 0-2Zm9 0a1 1 0 1 0 0 2 1 1 0 0 0 0-2Z"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
      {/* aria-hidden: the count is announced once, by the label below. Without this a
          screen reader reads "3" and then "Shopping cart, 3 items". */}
      {showBadge ? (
        <span className={css.badge} data-testid="cart-badge" aria-hidden="true">
          {label}
        </span>
      ) : null}
      <span className={css.srOnly}>
        <FormattedMessage id="CartIcon.label" values={{ count: itemCount }} />
      </span>
    </NamedLink>
  );
};

const mapStateToProps = state => ({
  itemCount: selectCartItemCount(state),
  initialized: selectCartInitialized(state),
});

export default connect(mapStateToProps)(CartIconComponent);
